import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

// Generate a unique invite code
const generateInviteCode = () => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function signup(email: string, password: string, displayName: string) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update display name
    await updateProfile(result.user, { displayName });
    
    // Create user profile in Firestore
    const userData: User = {
      uid: result.user.uid,
      email: result.user.email!,
      displayName,
      photoURL: result.user.photoURL || undefined,
      createdAt: new Date(),
      lastActive: new Date(),
      isActive: true,
      preferences: {
        language: 'en',
        notifications: {
          email: true,
          push: true,
          newPostcards: true,
          replies: true,
        },
        privacy: {
          showOnLeaderboard: true,
          allowRandomMatches: true,
          allowFriendRequests: true
        }
      },
      stats: {
        postcardsSent: 0,
        postcardsReceived: 0,
        countriesReached: 0,
        totalPoints: 0,
        achievements: [],
        friendsCount: 0,
        replyRate: 0
      },
      friends: [],
      inviteCode: generateInviteCode(),
      dailySendsCount: 0,
      lastSendDate: new Date()
    };

    await setDoc(doc(db, 'users', result.user.uid), userData);
  }

  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        // Create new user profile
        const userData: User = {
          uid: result.user.uid,
          email: result.user.email!,
          displayName: result.user.displayName || 'Anonymous',
          photoURL: result.user.photoURL || undefined,
          createdAt: new Date(),
          lastActive: new Date(),
          isActive: true,
          preferences: {
            language: 'en',
            notifications: {
              email: true,
              push: true,
              newPostcards: true,
              replies: true,
            },
            privacy: {
              showOnLeaderboard: true,
              allowRandomMatches: true,
              allowFriendRequests: true
            }
          },
          stats: {
            postcardsSent: 0,
            postcardsReceived: 0,
            countriesReached: 0,
            totalPoints: 0,
            achievements: [],
            friendsCount: 0,
            replyRate: 0
          },
          friends: [],
          inviteCode: generateInviteCode(),
          dailySendsCount: 0,
          lastSendDate: new Date()
        };

        await setDoc(doc(db, 'users', result.user.uid), userData);
        setUserProfile(userData);
      } else {
        // Update last active time
        const userData = userDoc.data() as User;
        await setDoc(doc(db, 'users', result.user.uid), {
          ...userData,
          lastActive: new Date(),
        }, { merge: true });
        setUserProfile({ ...userData, lastActive: new Date() });
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  async function logout() {
    await signOut(auth);
  }

  async function updateUserProfile(data: Partial<User>) {
    if (!currentUser) return;
    
    const userRef = doc(db, 'users', currentUser.uid);
    await setDoc(userRef, data, { merge: true });
    
    // Update local state
    if (userProfile) {
      setUserProfile({ ...userProfile, ...data });
    }
  }

  async function fetchUserProfile(uid: string) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data() as User;
        setUserProfile(data);
      } else {
        // If profile doesn't exist, set to null (will be created on next action)
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    signup,
    logout,
    loginWithGoogle,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 